import { useState } from 'react'

import { ApiError } from '@/lib/api'
import { usePolicyOrder, PolicyPositionPicker, PolicySelector, PRINTER_SELECTOR_FIELDS } from '@/modules/hierarchy'
import { Button } from '@/ui/button'
import { DialogDescription, DialogTitle } from '@/ui/dialog'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { TextField } from '@/ui/text-field'
import { DEFAULT_FORMAT_ORDER, DEFAULT_COUNTERS } from '../../lib/print-settings/print-settings'
import { PREFERRED_COUNTER_POLICY_TYPE, type PreferredCounterPolicy, type PreferredCounterEdit } from '../../api/preferred-counter-policy-api'
import { PreferredCounterFields } from './preferred-counter-fields'
import { preferredCounterPolicyError, useSavePreferredCounterPolicy } from './queries'

export function PreferredCounterPolicyForm({
  entityId,
  policy,
  onClose,
  onReload,
  reloading = false,
  reloadError = null,
}: {
  entityId: string
  policy: PreferredCounterPolicy | null
  onClose: () => void
  onReload?: () => void
  reloading?: boolean
  reloadError?: string | null
}) {
  const save = useSavePreferredCounterPolicy(entityId)
  const order = usePolicyOrder(entityId, PREFERRED_COUNTER_POLICY_TYPE, policy === null)
  const [beforePolicyId, setBeforePolicyId] = useState<string | null>(null)
  const [operationId] = useState(() => crypto.randomUUID())
  const [attemptedCreate, setAttemptedCreate] = useState<PreferredCounterEdit | null>(null)
  const [uncertainCreate, setUncertainCreate] = useState(false)
  const [name, setName] = useState(policy?.name ?? '')
  const [selectorExpression, setSelectorExpression] = useState(policy?.selectorExpression ?? '')
  const [selectorGui, setSelectorGui] = useState(policy?.selectorGui ?? null)
  const [selectorValid, setSelectorValid] = useState(true)
  const [value, setValue] = useState(policy?.value ?? {
    formatPriority: [...DEFAULT_FORMAT_ORDER],
    countersToDisplay: [...DEFAULT_COUNTERS],
  })
  const pending = save.isPending || reloading
  const fieldsLocked = pending || uncertainCreate
  const countersValid = value.countersToDisplay.length > 0
  const orderReady = policy !== null || order.data !== undefined
  const currentBeforePolicyId = order.data?.some((entry) => entry.id === beforePolicyId)
    ? beforePolicyId
    : null
  const title = policy ? `Edit ${policy.name}` : 'Create policy'
  const submitLabel = uncertainCreate ? 'Retry same policy' : 'Save policy'

  function submit() {
    if (pending || !name.trim() || !orderReady || !selectorValid || !countersValid) return
    const edit = attemptedCreate ?? {
      name: name.trim(),
      value: { ...value },
      selectorExpression: selectorExpression.trim() || null,
      selectorGui: selectorExpression.trim() ? selectorGui : null,
      ...(policy === null && order.data ? { placement: {
        beforePolicyId: currentBeforePolicyId,
        expectedOrder: order.data.map(({ id, revision }) => ({ id, revision })),
      } } : {}),
    }
    if (policy === null) setAttemptedCreate(edit)
    save.mutate(
      { policy, operationId, edit },
      {
        onSuccess: onClose,
        onError: handleCreateFailure,
      },
    )
  }

  function handleCreateFailure(error: unknown) {
    if (policy !== null) return
    const confirmsNoCommittedCreate = error instanceof ApiError && error.code === 'POLICY_ORDER_CONFLICT'
    const rejectionIsCertain = isDefiniteCreateRejection(error)
      && (!uncertainCreate || confirmsNoCommittedCreate)
    if (rejectionIsCertain) {
      setAttemptedCreate(null)
      setUncertainCreate(false)
      return
    }
    setUncertainCreate(true)
  }

  return (
    <form
      className="flex flex-col gap-6"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          {policy ? <DialogTitle>{title}</DialogTitle> : <h2 className="text-sm font-medium">2. Configure preferred counters</h2>}
          {policy ? (
            <DialogDescription>Choose which counters new printers display and the order used to select their values.</DialogDescription>
          ) : <p className="text-muted-foreground text-xs">Choose which counters new printers display and the order used to select their values.</p>}
        </div>
        <TextField
          name="name"
          label="Policy name"
          kind="text"
          required
          maxLength={200}
          value={name}
          disabled={fieldsLocked}
          onChange={setName}
          onBlur={() => undefined}
        />
        <PreferredCounterFields entityId={entityId} value={value} onChange={setValue} disabled={fieldsLocked} />
      </section>
      <PolicySelector
        targetFields={PRINTER_SELECTOR_FIELDS}
        targetLabel="printers"
        entityId={entityId}
        value={selectorExpression}
        gui={selectorGui}
        disabled={fieldsLocked}
        onChange={(expression, valid, gui) => {
          setSelectorExpression(expression)
          setSelectorValid(valid)
          setSelectorGui(gui)
        }}
      />
      {policy === null && (
        <>
          {order.isLoading && <LoadingNotice message="Loading policy order…" />}
          {order.isError && <RetryNotice message="Couldn’t load the policy order." onRetry={() => void order.refetch()} />}
          {order.data && (
            <PolicyPositionPicker
              policies={order.data}
              name={name}
              beforePolicyId={currentBeforePolicyId}
              onChange={setBeforePolicyId}
              disabled={fieldsLocked}
            />
          )}
        </>
      )}
      {uncertainCreate && (
        <p role="status" className="text-muted-foreground text-xs">
          This request may have already succeeded. Retry it to confirm, or cancel and refresh
          the list before creating another policy.
        </p>
      )}
      {save.isError && (
        <p role="alert" className="text-destructive text-xs">
          {preferredCounterPolicyError(save.error)}
        </p>
      )}
      {save.isError && save.error instanceof ApiError && save.error.code === 'POLICY_ORDER_CONFLICT' && policy === null && (
        <Button type="button" variant="outline" disabled={pending} onClick={() => {
          void order.refetch().then((result) => {
            if (!result.isSuccess) return
            setBeforePolicyId(null)
            save.reset()
          })
        }}>Reload policy order</Button>
      )}
      {reloadError && (
        <p role="alert" className="text-destructive text-xs">
          {reloadError}
        </p>
      )}
      <div className="border-border flex justify-end gap-2 border-t pt-4">
        {onReload && (
          <Button type="button" variant="ghost" disabled={pending} onClick={onReload}>
            Reload latest policy
          </Button>
        )}
        <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending || !name.trim() || !orderReady || !selectorValid || !countersValid}>
          {save.isPending ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  )
}

function isDefiniteCreateRejection(error: unknown) {
  if (!(error instanceof ApiError)) return false
  if (error.code === 'PRINTER_POLICY_CONFLICT') return false
  return [400, 403, 404, 409].includes(error.status)
    || error.code === 'PRINTER_POLICY_BUSY'
    || error.code === 'PRINTER_POLICY_EVALUATOR_UNAVAILABLE'
}
